import { MEDIA_BASE_URL } from "../config";

const fileCache: Record<string, string> = {};

/**
 * Converts a WebSocket URL to its HTTP base URL
 * @param socketUrl - WebSocket URL (ws:// or wss://)
 * @returns HTTP base URL (http:// or https://)
 */
export function httpBaseFromSocketUrl(socketUrl: string): string {
  try {
    const url = new URL(socketUrl);
    const protocol =
      url.protocol === "wss:"
        ? "https:"
        : url.protocol === "ws:"
        ? "http:"
        : url.protocol;
    return `${protocol}//${url.host}`;
  } catch {
    return "";
  }
}

/**
 * Fetches a file by ID and returns it as a data URL
 * Results are cached to avoid re-fetching the same file
 * @param id - The file ID (UUID or URL)
 * @returns Promise with data URL string, or null on error
 */
export async function getFileById(id: string): Promise<string | null> {
  try {
    // Check cache first
    if (fileCache[id]) return fileCache[id];

    // If not a UUID, assume it's already a URL
    if (
      !id.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
      )
    ) {
      return id;
    }

    const response = await fetch(`${MEDIA_BASE_URL}/get/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch file with id: ${id}`);
    }

    const blob = await response.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(blob);
    });

    fileCache[id] = base64;
    return base64;
  } catch (e) {
    return null;
  }
}
